/**
 * React Query hook + types for Stores > Item Sales (/retail/item-sales).
 *
 * Item Sales by Store answers "what sold, at which store, by SKU, over a date
 * range": one row per store per SKU for the WHOLE selected range (not one row
 * per day), with units sold, net sales, cost, and ticket count. The user picks
 * a from and a to date (both defaulting to yesterday), chooses which kinds of
 * items to include, and narrows by store, category, subcategory, and SKU or
 * description text.
 *
 *   useItemSales(filters, sort, enabled)  GET /v1/retail/item-sales
 *   downloadItemSalesXlsx(filters, sort)  GET /v1/retail/item-sales/export
 *
 * The daily-email subscription for this report goes through the generic
 * `useReportSubscriptions` hook, not through here. What lives here is the pure
 * pair that decides WHICH filters a subscription saves
 * (`itemSalesSubscriptionFilters`, which drops the date range because the send
 * re-resolves the day every morning) and how two saved filter sets compare
 * (`itemSalesFiltersKey`).
 *
 * This file is the SINGLE reconcile point for the api contract (spec
 * `00_item-sales-by-store.md`, Tasks 4 and 5). Any contract drift is fixed
 * here, in the types, the query-string builder, and the normalizers, and the
 * page follows.
 *
 * Contract:
 *
 *   GET /v1/retail/item-sales
 *     ?date_from=YYYY-MM-DD     first business day of the range
 *     &date_to=YYYY-MM-DD       last business day of the range (inclusive)
 *     &item_types=I,D           which CounterPoint item types to include,
 *                               defaults to I,D (merchandise + discounts)
 *     &category=...             optional comma list of category names
 *     &subcategory=...          optional comma list of subcategory names
 *     &store_no=...             optional, repeatable (a FILTER, never a scope)
 *     &item_no=...              optional, exact SKU
 *     &q=...                    optional, SKU or description contains
 *     &sort=...&dir=asc|desc    optional column sort, owned by the backend
 *     &limit=...&cursor=...     keyset paging for infinite scroll
 *   -> {
 *        as_of,        // date string, or NULL when the range holds no data
 *        date,         // legacy single-day echo, kept for older callers
 *        date_from,    // first day the rows cover
 *        date_to,      // last day the rows cover
 *        scope,        // 'fleet' for every capability holder
 *        filters,      // the filters the backend actually applied
 *        rows: [ { store_no, store_name, item_no, descr, category,
 *                  subcategory, qty_sold, net_sales, ext_cost, tkt_count } ],
 *        total_count,  // full match count, drives the DataGridShell count line
 *        totals: { qty_sold, net_sales, ext_cost, tkt_count },
 *        stores: [ { store_no, store_name } ],
 *        categories: [ { category, subcategory } ],
 *        next_cursor,
 *      }
 *
 * The api still accepts a single `date` as an alias for both ends of the range,
 * which is what keeps old bookmarks working: `resolveItemSalesRange` reads a
 * legacy `date` URL param into both `date_from` and `date_to`.
 *
 * `as_of` is null when the chosen range has no data, which drives the page's
 * empty state. `ext_cost` and `tkt_count` are nullable per row: the two online
 * (DTC) stores have no per-SKU ticket count and may have no cost, and the spec
 * requires NULL there rather than a 0 that would read as a real zero in a sum.
 *
 * Item types: CounterPoint types every sales row as `I` (inventory
 * merchandise), `N` (non-inventory, which is where bag fees live) or `D`
 * (discount). The page ships with bag fees EXCLUDED because a single bag-fee
 * SKU carried 306,471 units at $0 over three months and swamped the units
 * column (Steven, 2026-08-04). The three presets are exposed in the operator's
 * language rather than as CounterPoint letters.
 *
 * Scope: holding `retail.itemsales.read` grants the fleet view (Steven,
 * 2026-08-04). There is no store-manager own-store path, so `store_no` is only
 * ever a filter. A caller without the capability gets 403 and reaches no rows.
 *
 * Resilience: `retry: false`, stable empty defaults, and tolerant normalizers
 * so the page degrades to a "not available yet" state on any 404 / 503 / shape
 * mismatch instead of crashing.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';
import type { ReportPageStateEntry } from './reportPageState';

export const ITEM_SALES_API_BASE = `${API_BASE}/v1/retail/item-sales`;

/** Rows fetched per page. Feeds the DataGridShell infinite scroll. */
export const ITEM_SALES_PAGE_SIZE = 100;

/** ISO calendar date, the only date shape this report accepts. */
export const ITEM_SALES_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Item types (CounterPoint letters, presented in the operator's language)
// ---------------------------------------------------------------------------

/** A CounterPoint item type as the api expects it. */
export type ItemTypeCode = 'I' | 'N' | 'D';

/** The three choices the page offers. Named for what they mean, not the letters. */
export type ItemTypePreset = 'merch_discounts' | 'merch_only' | 'everything';

export type ItemTypePresetDef = {
  preset: ItemTypePreset;
  /** Operator-facing label. Steven's words, not CounterPoint's. */
  label: string;
  codes: ItemTypeCode[];
};

/**
 * What each preset means. `merch_discounts` is the default: it keeps
 * merchandise and the discounts taken against it, and drops the non-inventory
 * rows (bag fees, gift wrap, shipping) that distort units.
 */
const ITEM_TYPE_PRESET_DEFS: Record<ItemTypePreset, { label: string; codes: ItemTypeCode[] }> = {
  merch_discounts: { label: 'Merchandise and discounts', codes: ['I', 'D'] },
  merch_only: { label: 'Merchandise only', codes: ['I'] },
  everything: { label: 'Everything, including bag fees', codes: ['I', 'N', 'D'] },
};

/** Menu order for the selector. Least polluted view first. */
export const ITEM_TYPE_PRESET_ORDER: ReadonlyArray<ItemTypePreset> = [
  'merch_discounts',
  'merch_only',
  'everything',
];

/** The three menu entries, ready to render. */
export const ITEM_TYPE_PRESETS: ReadonlyArray<ItemTypePresetDef> = ITEM_TYPE_PRESET_ORDER.map(
  (preset) => ({ preset, ...ITEM_TYPE_PRESET_DEFS[preset] }),
);

export const DEFAULT_ITEM_TYPE_PRESET: ItemTypePreset = 'merch_discounts';

const VALID_ITEM_TYPES: ReadonlySet<string> = new Set<ItemTypeCode>(['I', 'N', 'D']);

/** A preset's codes, in the order the api reads them. */
export function itemTypeCodesFor(preset: ItemTypePreset): ItemTypeCode[] {
  return [...ITEM_TYPE_PRESET_DEFS[preset].codes];
}

/** A preset's operator-facing label. */
export function itemTypeLabelFor(preset: ItemTypePreset): string {
  return ITEM_TYPE_PRESET_DEFS[preset].label;
}

/** Canonical comparison key for a set of codes: uppercased, deduped, sorted. */
function codeKey(codes: readonly string[]): string {
  return [...new Set(codes.map((c) => c.trim().toUpperCase()).filter((c) => VALID_ITEM_TYPES.has(c)))]
    .sort()
    .join(',');
}

/**
 * Reads the URL's `item_types` value (a comma list of letters, e.g. `I,D`) back
 * into one of the three presets, so a shared link reproduces the operator's
 * choice. Anything unrecognized, empty, or outside the three offered sets falls
 * back to the default rather than sending a code list the menu cannot render.
 */
export function itemTypePresetFromParam(raw: string | null | undefined): ItemTypePreset {
  if (typeof raw !== 'string') return DEFAULT_ITEM_TYPE_PRESET;
  const key = codeKey(raw.split(','));
  if (!key) return DEFAULT_ITEM_TYPE_PRESET;
  const hit = ITEM_TYPE_PRESETS.find((p) => codeKey(p.codes) === key);
  return hit ? hit.preset : DEFAULT_ITEM_TYPE_PRESET;
}

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

function validDate(v: unknown): string | null {
  return typeof v === 'string' && ITEM_SALES_DATE_RE.test(v) ? v : null;
}

/**
 * Works out the range to query from whatever the URL carries.
 *
 * Precedence per end: an explicit `date_from` / `date_to`, then a legacy single
 * `date` (which the first version of this page wrote, so old bookmarks land on
 * that one day at both ends), then the caller's fallback (yesterday). A
 * reversed range is swapped rather than rejected, so a hand-edited URL still
 * returns rows.
 */
export function resolveItemSalesRange(
  rawFrom: string | null | undefined,
  rawTo: string | null | undefined,
  rawLegacyDate: string | null | undefined,
  fallback: string,
): { date_from: string; date_to: string } {
  const legacy = validDate(rawLegacyDate);
  let from = validDate(rawFrom) ?? legacy ?? fallback;
  let to = validDate(rawTo) ?? legacy ?? fallback;
  if (from > to) [from, to] = [to, from];
  return { date_from: from, date_to: to };
}

// ---------------------------------------------------------------------------
// Types (the spec Task 4 contract; reconcile here on any drift)
// ---------------------------------------------------------------------------

/** One store x SKU row, aggregated over the whole selected date range. */
export type ItemSalesRow = {
  store_no: string;
  store_name: string | null;
  item_no: string;
  descr: string | null;
  category: string | null;
  subcategory: string | null;
  qty_sold: number | null;
  net_sales: number | null;
  ext_cost: number | null;
  tkt_count: number | null;
};

/** Grand totals over every matching row, not just the loaded page. */
export type ItemSalesTotals = {
  qty_sold: number | null;
  net_sales: number | null;
  ext_cost: number | null;
  tkt_count: number | null;
};

/** Sortable columns. Same names as the row fields, so the api can map 1:1. */
export type ItemSalesSortColumn =
  | 'store_no'
  | 'item_no'
  | 'descr'
  | 'category'
  | 'subcategory'
  | 'qty_sold'
  | 'net_sales'
  | 'ext_cost'
  | 'tkt_count';

export const ITEM_SALES_SORTABLE: ReadonlySet<string> = new Set<ItemSalesSortColumn>([
  'store_no',
  'item_no',
  'descr',
  'category',
  'subcategory',
  'qty_sold',
  'net_sales',
  'ext_cost',
  'tkt_count',
]);

export type ItemSalesSort = { column: ItemSalesSortColumn; dir: 'asc' | 'desc' };

/**
 * Column order and labels for the sort menu. The mobile card list has no
 * clickable column headers, so the Sort control reads from here and the table's
 * `SortableHeader` columns read the same names: one list, so a phone can sort by
 * exactly what a desktop can sort by, and the URL that results is identical.
 */
export const ITEM_SALES_SORT_OPTIONS: ReadonlyArray<{
  column: ItemSalesSortColumn;
  label: string;
}> = [
  { column: 'store_no', label: 'Store' },
  { column: 'item_no', label: 'SKU' },
  { column: 'descr', label: 'Description' },
  { column: 'category', label: 'Category' },
  { column: 'subcategory', label: 'Subcategory' },
  { column: 'qty_sold', label: 'Units sold' },
  { column: 'net_sales', label: 'Net sales' },
  { column: 'ext_cost', label: 'Cost' },
  { column: 'tkt_count', label: 'Tickets' },
];

/**
 * Reads the URL's `sort=<column>:<dir>` param. Returns null for anything the
 * backend does not sort by, so a hand-edited or stale link degrades to the
 * default order instead of sending the api a column it will reject.
 */
export function parseItemSalesSort(raw: string | null | undefined): ItemSalesSort | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !ITEM_SALES_SORTABLE.has(col)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as ItemSalesSortColumn, dir };
}

/** The inverse of `parseItemSalesSort`: the `sort` param for a chosen sort. */
export function formatItemSalesSort(sort: ItemSalesSort): string {
  return `${sort.column}:${sort.dir}`;
}

export type ItemSalesFilters = {
  /** First day of the range, ISO YYYY-MM-DD. */
  date_from: string;
  /** Last day of the range, inclusive, ISO YYYY-MM-DD. */
  date_to: string;
  /**
   * Which CounterPoint item types to include. Always sent explicitly, even at
   * the default, so the grid and the Excel export can never disagree about
   * whether bag fees are in.
   */
  item_types: ItemTypeCode[];
  /** Repeatable store filter. Never a scope boundary. */
  store_no?: string[];
  /** Category names. */
  category?: string[];
  /** Subcategory names. */
  subcategory?: string[];
  /** Exact SKU. */
  item_no?: string;
  /** SKU or description contains. */
  q?: string;
};

export type ItemSalesResponse = {
  as_of?: string | null;
  /** Legacy single-day echo. Kept readable but no longer what the page shows. */
  date?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  scope?: string | null;
  filters?: unknown;
  // Optional on purpose: a payload that comes back without `rows` at all is a
  // shape mismatch the normalizer absorbs into an empty grid, rather than a
  // crash on a missing property.
  rows?: unknown;
  total_count?: number | null;
  totals?: unknown;
  /** Keyset cursor for the next page; absent or null on the last page. */
  next_cursor?: string | null;
  /**
   * Every store with data in the reported range, ordered by store_no, and NOT
   * narrowed by the store_no filter. This is the authoritative source for the
   * store picker's options: one page of store-by-SKU rows covers only a handful
   * of stores, and the store-master read excludes is_dtc = 1, so neither alone
   * surfaces the two online stores (DTC-LP, DTC-HM).
   */
  stores?: unknown;
  /**
   * Every category / subcategory pair present in the range, NOT narrowed by the
   * category or subcategory filters. Self-narrowing options would strand the
   * operator: once a category is chosen, the other 13 would vanish from the
   * menu and could not be chosen again.
   */
  categories?: unknown;
};

/** One entry of the response's store option list. */
export type ItemSalesStoreOption = { store_no: string; store_name: string | null };

/** One category / subcategory pair from the response's taxonomy list. */
export type ItemSalesCategoryOption = { category: string; subcategory: string | null };

// ---------------------------------------------------------------------------
// Query string
// ---------------------------------------------------------------------------

/**
 * Builds the query string shared by the JSON read and the Excel export, so a
 * download can never disagree with what is on screen. `sort`/`dir` are only
 * sent when the operator has picked a column: with no sort the backend's own
 * default ordering applies.
 *
 * `store_no` repeats (one param per store, which is how the first version
 * shipped); `item_types`, `category` and `subcategory` are comma lists, which
 * is what the api reads.
 */
export function buildItemSalesQs(
  f: ItemSalesFilters,
  sort: ItemSalesSort | null,
  cursor: string | null,
  limit: number | null,
): string {
  const p = new URLSearchParams();
  if (f.date_from) p.set('date_from', f.date_from);
  if (f.date_to) p.set('date_to', f.date_to);
  if (f.item_types && f.item_types.length > 0) p.set('item_types', f.item_types.join(','));
  for (const s of f.store_no ?? []) p.append('store_no', s);
  if (f.category && f.category.length > 0) p.set('category', f.category.join(','));
  if (f.subcategory && f.subcategory.length > 0) p.set('subcategory', f.subcategory.join(','));
  if (f.item_no) p.set('item_no', f.item_no);
  if (f.q) p.set('q', f.q);
  if (sort) {
    p.set('sort', sort.column);
    p.set('dir', sort.dir);
  }
  if (limit != null) p.set('limit', String(limit));
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

// ---------------------------------------------------------------------------
// Daily-email subscription (pure helpers; the API calls live in
// useReportSubscriptions.ts, which is the one place report subscriptions are
// created and removed)
// ---------------------------------------------------------------------------

/**
 * The filter state saved with a daily-email subscription to this report.
 *
 * `date_from` and `date_to` are DELIBERATELY LEFT OUT. The daily send always
 * covers the most recent complete business day and re-resolves that day every
 * morning, so freezing the range that happens to be on screen would make the
 * subscriber a permanent report about one stale week. Everything else the
 * operator narrowed by is kept, because those choices are what makes one
 * subscription different from another.
 *
 * Empty arrays and blank strings are dropped rather than sent, so "no store
 * filter" reaches the api as an absent key instead of an empty list that reads
 * as "no stores at all".
 *
 * MULTI-SELECTS ARE SENT AS COMMA STRINGS, NOT ARRAYS, and that is load-bearing.
 * The api's `normalizeFilters` (`report-subscription-filters.ts`) keeps scalars
 * only and drops arrays outright, so posting `store_no: ['1129','1131']` stored
 * NOTHING: the subscription saved with no store filter and the morning email
 * arrived covering the whole fleet, silently, with the grid still showing the
 * filter the operator thought they had saved. Comma strings are also the form
 * the digest's own query builder reads back. If a future filter is multi-valued,
 * join it here too.
 */
export function itemSalesSubscriptionFilters(f: ItemSalesFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.store_no && f.store_no.length > 0) out.store_no = f.store_no.join(',');
  if (f.category && f.category.length > 0) out.category = f.category.join(',');
  if (f.subcategory && f.subcategory.length > 0) out.subcategory = f.subcategory.join(',');
  if (f.item_types && f.item_types.length > 0) out.item_types = f.item_types.join(',');
  if (f.item_no) out.item_no = f.item_no;
  if (f.q) out.q = f.q;
  return out;
}

/**
 * A canonical, comparable rendering of one saved filter set, used to tell
 * whether the caller already holds a subscription matching the view in front of
 * them (which is what turns the button into Unsubscribe).
 *
 * Keys sort, list values sort, and a list is joined with commas, so a saved
 * `['B','A']` and a saved `'A,B'` produce the same key. That matters because the
 * api may store a repeatable filter either way, and a comparison that cared
 * would silently offer a second identical subscription. Blank and empty values
 * drop out, matching `itemSalesSubscriptionFilters` above.
 */
export function itemSalesFiltersKey(filters: Record<string, unknown> | null | undefined): string {
  if (!filters || typeof filters !== 'object') return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    const val = Array.isArray(v)
      ? v
          .map((x) => String(x))
          .sort()
          .join(',')
      : String(v);
    if (val === '') continue;
    parts.push(`${k}=${val}`);
  }
  return parts.sort().join('&');
}

// ---------------------------------------------------------------------------
// Normalizers (tolerant reads so a partial/absent payload degrades cleanly)
// ---------------------------------------------------------------------------

/**
 * Reads a numeric field that may arrive as a JSON number or as a string
 * (MySQL DECIMAL columns serialize as text). Returns null for anything that is
 * not a finite number, so a missing cost stays missing rather than becoming 0.
 */
function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** A trimmed non-empty string, or null. Used for taxonomy names. */
function name(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
}

/**
 * Reads the raw `rows` array into typed rows, dropping any entry without both
 * a store number and an item number (the two halves of the row's identity).
 * Returns [] on any shape mismatch. Order is preserved: the backend owns the
 * sort, and re-sorting client-side would break infinite scroll by reordering
 * only the pages loaded so far.
 */
export function normalizeItemSalesRows(raw: unknown): ItemSalesRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: ItemSalesRow[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const store_no = str(o.store_no) ?? (typeof o.store_no === 'number' ? String(o.store_no) : null);
    const item_no = str(o.item_no) ?? (typeof o.item_no === 'number' ? String(o.item_no) : null);
    if (!store_no || !item_no) continue;
    rows.push({
      store_no,
      store_name: str(o.store_name),
      item_no,
      descr: str(o.descr),
      category: name(o.category),
      subcategory: name(o.subcategory),
      qty_sold: num(o.qty_sold),
      net_sales: num(o.net_sales),
      ext_cost: num(o.ext_cost),
      tkt_count: num(o.tkt_count),
    });
  }
  return rows;
}

/**
 * Reads the `stores` option list, dropping any entry without a store number and
 * returning [] on any shape mismatch, so an older backend that predates the
 * field degrades to the page's fallback rather than throwing.
 */
export function normalizeItemSalesStores(raw: unknown): ItemSalesStoreOption[] {
  if (!Array.isArray(raw)) return [];
  const out: ItemSalesStoreOption[] = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const o = s as Record<string, unknown>;
    const store_no =
      str(o.store_no) ?? (typeof o.store_no === 'number' ? String(o.store_no) : null);
    if (!store_no) continue;
    out.push({ store_no, store_name: str(o.store_name) });
  }
  return out;
}

/**
 * Reads the `categories` taxonomy list into deduped, trimmed pairs. An entry
 * with no category name is dropped (there is nothing to filter on); a pair with
 * a category but no subcategory is kept, because those SKUs exist and the
 * category picker must still offer them. Returns [] on any shape mismatch so a
 * backend that predates the field simply leaves both pickers empty.
 */
export function normalizeItemSalesCategories(raw: unknown): ItemSalesCategoryOption[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ItemSalesCategoryOption[] = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const o = c as Record<string, unknown>;
    const category = name(o.category);
    if (!category) continue;
    const subcategory = name(o.subcategory);
    const key = `${category}\u0000${subcategory ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ category, subcategory });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Cascading taxonomy pickers (pure, so the page stays declarative)
// ---------------------------------------------------------------------------

function sortNames(values: Iterable<string>): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

/** Every category name in the taxonomy, deduped and alphabetical. */
export function itemSalesCategoryNames(pairs: readonly ItemSalesCategoryOption[]): string[] {
  return sortNames(pairs.map((p) => p.category));
}

/**
 * The subcategory names on offer, narrowed by the chosen categories. This is
 * the cascade: with no category chosen every subcategory is offered, and once
 * one or more categories are chosen only their subcategories remain. A category
 * name that is not in the taxonomy contributes nothing.
 */
export function itemSalesSubcategoryNames(
  pairs: readonly ItemSalesCategoryOption[],
  selectedCategories: readonly string[],
): string[] {
  const wanted = new Set(selectedCategories);
  const scoped = wanted.size === 0 ? pairs : pairs.filter((p) => wanted.has(p.category));
  return sortNames(scoped.map((p) => p.subcategory).filter((s): s is string => s != null));
}

/**
 * Drops any selected subcategory that the current category choice no longer
 * offers, so narrowing the category cannot leave an invisible filter pinned in
 * the URL that silently empties the grid.
 */
export function pruneItemSalesSubcategories(
  selected: readonly string[],
  available: readonly string[],
): string[] {
  const ok = new Set(available);
  return selected.filter((s) => ok.has(s));
}

/** Reads the `totals` object, returning all-null on any shape mismatch. */
export function normalizeItemSalesTotals(raw: unknown): ItemSalesTotals {
  const empty: ItemSalesTotals = { qty_sold: null, net_sales: null, ext_cost: null, tkt_count: null };
  if (!raw || typeof raw !== 'object') return empty;
  const o = raw as Record<string, unknown>;
  return {
    qty_sold: num(o.qty_sold),
    net_sales: num(o.net_sales),
    ext_cost: num(o.ext_cost),
    tkt_count: num(o.tkt_count),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fleet item sales for a date range. Keyset-paged for infinite scroll:
 * `fetchNextPage` follows the response's `next_cursor` and the loaded rows
 * accumulate across pages.
 *
 * Surfaces `asOf` (null when the range holds no data, which drives the page's
 * empty state), `totalCount` (the full match count for the count line),
 * `totals` (grand totals over every matching row, from the first page), and the
 * `stores` / `categories` option lists that populate the pickers.
 */
export function useItemSales(
  filters: ItemSalesFilters,
  sort: ItemSalesSort | null,
  enabled: boolean,
) {
  const query = useInfiniteQuery<ItemSalesResponse, Error>({
    queryKey: ['item-sales', filters, sort],
    queryFn: ({ pageParam }) =>
      apiGet<ItemSalesResponse>(
        `${ITEM_SALES_API_BASE}?${buildItemSalesQs(
          filters,
          sort,
          (pageParam as string | null) ?? null,
          ITEM_SALES_PAGE_SIZE,
        )}`,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });

  const pages = query.data?.pages ?? [];
  const first = pages[0];
  return {
    // `as_of` is only meaningful once a page has landed; while loading it stays
    // undefined so the page does not flash the empty state.
    asOf: first ? (first.as_of ?? null) : undefined,
    dateFrom: first?.date_from ?? first?.date ?? filters.date_from,
    dateTo: first?.date_to ?? first?.date ?? filters.date_to,
    scope: first?.scope ?? null,
    rows: pages.flatMap((p) => normalizeItemSalesRows(p.rows)),
    totalCount: first?.total_count ?? null,
    totals: normalizeItemSalesTotals(first?.totals),
    /** Authoritative store options for the picker; [] until a page lands. */
    stores: normalizeItemSalesStores(first?.stores),
    /** Category / subcategory pairs feeding the two cascading pickers. */
    categories: normalizeItemSalesCategories(first?.categories),
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Triggers a browser download of the Item Sales Excel export. Sends the same
 * filters and the same sort as the grid, so the workbook matches the view.
 * Mirrors downloadLeakageXlsx / downloadOmniXlsx.
 */
export async function downloadItemSalesXlsx(
  filters: ItemSalesFilters,
  sort: ItemSalesSort | null,
): Promise<void> {
  const url = `${ITEM_SALES_API_BASE}/export?${buildItemSalesQs(filters, sort, null, null)}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const range =
    filters.date_from === filters.date_to
      ? filters.date_from
      : `${filters.date_from}_${filters.date_to}`;
  const filename = m?.[1] ?? `item-sales-${range}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/**
 * Money for this report, to the cent: "$1,234.56", "--" when null.
 *
 * Deliberately NOT the shared `formatDollars` from useRetailStores: that one
 * rounds to whole dollars and collapses anything over a million to "$7.14M",
 * which is right for fleet-level sales but wrong here. A single SKU at a single
 * store is often a few dollars, so the cents carry the meaning and a rounded
 * figure would not reconcile against a register receipt.
 */
export function formatItemMoney(v: number | null | undefined): string {
  if (v == null) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

/** Units sold, up to 3 decimals (the column is DECIMAL(14,3)). Blank when null. */
export function formatItemUnits(v: number | null | undefined): string {
  if (v == null) return '--';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(v);
}

/** Whole ticket count. Blank when null, which is how online rows render. */
export function formatItemTickets(v: number | null | undefined): string {
  if (v == null) return '--';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(v));
}

/** Store column label: "1129 - Cherry Creek", or the bare number with no name. */
export function formatItemStore(storeNo: string, storeName: string | null | undefined): string {
  const name = storeName?.trim();
  return name ? `${storeNo} - ${name}` : storeNo;
}

// ---------------------------------------------------------------------------
// Page state for Support > Report an issue (ISD-357)
// ---------------------------------------------------------------------------

/**
 * Builds the human-readable page-state entries this page publishes via
 * `useReportPageState`, in the exact words the filter bar and the page's own
 * description already show. Fixes ISD-357: the ticket's Page state used to
 * show `item_types: I`, the raw URL parameter, when the operator had picked
 * "Merchandise only" on screen.
 *
 * Dates and Items included are always included, because this report has no
 * "unset" state for either: a range and an item-type preset are always
 * applied, default or not. Everything else (store, category, subcategory,
 * search, sort) is included only when the operator actually set it, mirroring
 * the same criteria the page's own `activeFilterCount` uses.
 */
export function buildItemSalesPageState(args: {
  dateFrom: string;
  dateTo: string;
  itemTypeLabel: string;
  storeLabels?: string[];
  categorySel?: string[];
  subcategorySel?: string[];
  q?: string;
  sort?: ItemSalesSort | null;
}): ReportPageStateEntry[] {
  const entries: ReportPageStateEntry[] = [
    {
      label: 'Dates',
      value: args.dateFrom === args.dateTo ? args.dateFrom : `${args.dateFrom} to ${args.dateTo}`,
    },
    { label: 'Items included', value: args.itemTypeLabel },
  ];
  if (args.storeLabels && args.storeLabels.length > 0) {
    entries.push({ label: 'Store', value: args.storeLabels.join(', ') });
  }
  if (args.categorySel && args.categorySel.length > 0) {
    entries.push({ label: 'Category', value: args.categorySel.join(', ') });
  }
  if (args.subcategorySel && args.subcategorySel.length > 0) {
    entries.push({ label: 'Subcategory', value: args.subcategorySel.join(', ') });
  }
  if (args.q) {
    entries.push({ label: 'Search', value: args.q });
  }
  if (args.sort) {
    const columnLabel = ITEM_SALES_SORT_OPTIONS.find((o) => o.column === args.sort!.column)?.label
      ?? args.sort.column;
    entries.push({
      label: 'Sort',
      value: `${columnLabel}, ${args.sort.dir === 'asc' ? 'ascending' : 'descending'}`,
    });
  }
  return entries;
}
